import React from 'react';
import { HistoryItem } from '../types';
import { X, Clock, ChevronRight, Trash2 } from 'lucide-react';

interface HistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  history: HistoryItem[];
  onSelect: (item: HistoryItem) => void;
  onClear: () => void;
}

const HistorySidebar: React.FC<HistorySidebarProps> = ({ isOpen, onClose, history, onSelect, onClear }) => {

  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
  };

  const getScoreColor = (score: number) => {
    if (score < 40) return 'bg-red-100 text-red-700 border-red-200';
    if (score < 75) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <div className={`fixed top-0 right-0 h-full w-full md:w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        
        {/* Header */}
        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Clock className="w-5 h-5 text-sky-500" />
              Historial de Análisis
            </h3>
            <p className="text-xs text-slate-500 mt-1">{history.length} análisis guardados</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-500 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {history.length === 0 ? (
            <div className="text-center py-10 text-slate-400 flex flex-col items-center">
              <Clock className="w-12 h-12 mb-3 opacity-20" />
              <p className="text-sm">No hay análisis guardados aún.</p>
            </div>
          ) : (
            history.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  onSelect(item);
                  onClose();
                }}
                className="w-full text-left bg-white border border-slate-100 rounded-xl p-4 hover:border-sky-300 hover:shadow-md transition-all group"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs text-slate-400 font-medium flex items-center gap-1">
                    {formatDate(item.timestamp)}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${getScoreColor(item.result.score)}`}>
                    {item.result.score}/100
                  </span>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2 font-medium group-hover:text-slate-800">
                  {item.inputText}
                </p>
                <div className="flex items-center text-xs text-sky-500 opacity-0 group-hover:opacity-100 transition-opacity gap-1 mt-2">
                  <span>Ver resultado</span>
                  <ChevronRight className="w-3 h-3" />
                </div>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        {history.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <button 
              onClick={onClear}
              className="w-full flex items-center justify-center gap-2 text-sm text-red-500 hover:text-red-700 font-medium py-2 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Borrar Historial
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default HistorySidebar;